import { BaseComponentProps } from '../types';
import { StoreDetails } from '../types/store';
import React, { useMemo, useState } from 'react';
import { loadStoreDetails, searchStore } from '../services/api/StoreService';
import { AxiosError } from 'axios';
import useNotification from '../hooks/context/useNotification';
import { DeliveryMode, OrderMethod } from '../types/constants';

type LoadStoreDataPayload =
  | { id: string; occPhoneNumber?: never }
  | { occPhoneNumber: string; id?: never };

export type StoreContextType = {
  loadingStore: boolean;
  storeId: null | string;
  storeData: null | StoreDetails;
  previewStoreData: null | StoreDetails;
  showSidebar: boolean;
  setStoreData: (value: null | StoreDetails) => void;
  onPreviewStore: (store: string | StoreDetails) => Promise<StoreDetails>;
  setPreviewStoreData: (value: null | StoreDetails) => void;
  setShowSidebar: (value: boolean) => void;
  loadStoreData: (payload: LoadStoreDataPayload) => Promise<StoreDetails>;
};

export const StoreContext = React.createContext<StoreContextType>(null);

const emptyStore: StoreDetails = {
  id: null,
  name: null,
  currency: null,

  dietary: [],
  has_reservations: false,
  has_internal_reservations: false,
  has_catering: false,
  has_terrace: false,
  has_dine_in: false,
  catering_min_people: 0,
  reservation_total_seats: 0,

  franchise: {
    id: null,
    stores: [],
  },

  location: {
    address: null,
    city: null,
    state: null,
    country: null,
    zip_code: null,
    formatted_address: null,

    lat: null,
    lng: null,
  },

  order_settings: {
    has_live_orders: false,
    has_asap_orders: false,

    has_scheduled_orders: false,
    has_pre_order: false,
    max_pre_order_days: 0,
    scheduled_orders_time_slot_interval: 0,

    has_pickup: false,
    has_delivery: false,
    has_on_site: false,
    preparation_time: {
      [OrderMethod.Pickup]: { min: 0, max: null },
      [OrderMethod.Delivery]: { min: 0, max: null },
    },

    delivery_mode: DeliveryMode.Internal,
    delivery_zones: [],
    delivery_providers: [],
  },

  payment_settings: {
    has_online_payments: false,
    has_cash_payments: false,
    has_in_store_card_payments: false,
  },

  availability: {
    open_now: false,
    has_delivery_schedule: false,
    work_schedule: [],
    delivery_schedule: null,
  },

  google_place: {
    id: null,
    rating: 0,
    rates_count: 0,
  },

  branding: {
    cover_image: null,
  },

  contacts: {
    email: null,
    name: null,
    phone_number: null,
  },

  links: {
    shop: null,
    website: null,
    reservation: null,
  },

  notes: [],

  promoted_products: [],
};

export default function StoreProvider({ children }: BaseComponentProps) {
  const [data, setData] = useState<StoreDetails>(emptyStore);
  const [previewStoreData, setPreviewStoreData] = useState<StoreDetails>(null);

  const { notificationApi } = useNotification();

  const [loading, setLoading] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);

  const loadStoreData = async ({
    id,
    occPhoneNumber,
  }: LoadStoreDataPayload): Promise<StoreDetails> => {
    const onSuccess = (_data: StoreDetails): StoreDetails => {
      setData(_data);

      return _data;
    };
    const onError = (error: AxiosError): null => {
      notificationApi.error({ message: 'Failed to load store data' });
      console.error('Failed to load store data', { error });

      return null;
    };

    setLoading(true);

    return (id ? loadStoreDetails(id) : searchStore({ occ_phone_number: occPhoneNumber }))
      .then(({ data: _data }) => onSuccess(_data))
      .catch((error) => onError(error))
      .finally(() => setLoading(false));
  };

  const onPreviewStore = async (store: string | StoreDetails): Promise<StoreDetails> => {
    const onSuccess = (data: StoreDetails): StoreDetails => {
      setPreviewStoreData(data);
      setShowSidebar(true);

      return data;
    };

    const storeId = typeof store === 'string' ? store : store.id;

    if (storeId === previewStoreData?.id) {
      setShowSidebar(true);

      return previewStoreData;
    }

    if (typeof store === 'string') {
      return loadStoreDetails(store).then(({ data }) => onSuccess(data));
    }

    onSuccess(store);
    loadStoreDetails(store.id).then(({ data }) => onSuccess(data));
  };

  const context = useMemo(
    () => ({
      loadingStore: loading,
      storeId: data?.id,
      storeData: data,
      previewStoreData,
      showSidebar,
      setStoreData: setData,
      onPreviewStore,
      setPreviewStoreData,
      setShowSidebar,
      loadStoreData,
    }),
    [loading, data, previewStoreData, showSidebar],
  );

  return <StoreContext.Provider value={context}>{children}</StoreContext.Provider>;
}
